/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

import space from '@amblique/king-living/app/theme/foundations/space';
import sizes from '@amblique/king-living/app/theme/foundations/sizes';
import {
  PRODUCT_TILE_CAROUSEL_MOBILE_SLIDE_WIDTH_PX,
} from '@amblique/king-living/app/constants/product-tiles-layout';
import {
  SWIPER_CF010_AUTOPLAY_ICON_PX,
  SWIPER_INSTOCK_NAV_NEXT,
  SWIPER_INSTOCK_NAV_PREV,
  SWIPER_NAV_MOBILE,
  swiperNavMobileIconSize,
} from '@amblique/king-living/app/theme/foundations/swiper-nav-tokens';
import zIndex from '@amblique/king-living/app/theme/foundations/z-index';

const helpers = createMultiStyleConfigHelpers([
  'root', 'container', 'slide', 'paginationContainer', 'navigation', 'pagination', 'scrollbar', 'autoplay'
]);

const SWIPER_NAV_BG = {
  arrow12x14: `${space[3]} ${space[3.5]}`,
  thumb14: space[3.5],
};

/** In-stock tile nav chevrons — backgroundSize for 12×13px icon box (md+) */
const SWIPER_INSTOCK_NAV_ICON_SIZE = `${space[3]} ${space[3.25]}`;

export default helpers.defineMultiStyleConfig({
  baseStyle: ({theme, presets = [], options = {}}) => {
    const presetList = Array.isArray(presets) ? presets : [];
    const isInstockTile = presetList.includes('instock_tile');
    const isRangeTile = presetList.includes('range_tile');
    const isRangeTileBlog = presetList.includes('range_tile_blog');
    const isRangeTileCarouselSlide = presetList.includes('range_tile_carousel_slide');
    const isWantItSooner = presetList.includes('want_it_sooner');
    /** Instock PDP tile + Range PLP tile + blog card tile share the same swiper chrome. */
    const instockLikeImageTile = isInstockTile || isRangeTile || isRangeTileBlog || isRangeTileCarouselSlide;

    const instockTileNavigation = {
      alignItems: 'center',
      backdropFilter: {base: `blur(${SWIPER_NAV_MOBILE.backdropBlur})`, md: 'blur(1px)'},
      bg: 'whiteAlpha.400',
      borderRadius: 'unset',
      cursor: 'pointer',
      display: 'flex',
      height: {base: SWIPER_NAV_MOBILE.button, md: 11},
      justifyContent: 'center',
      margin: '0',
      opacity: 1,
      p: {base: SWIPER_NAV_MOBILE.padding, md: 1},
      transform: 'translateY(-50%)',
      transition: 'all 0.2s ease',
      width: {base: SWIPER_NAV_MOBILE.button, md: 11},

      _hover: {
        bg: {base: 'whiteAlpha.550', md: 'whiteAlpha.650'},
      },

      _after: {
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: {base: swiperNavMobileIconSize, md: SWIPER_INSTOCK_NAV_ICON_SIZE},
        content: '""',
        display: 'block',
        height: {base: SWIPER_NAV_MOBILE.iconH, md: 3.25},
        width: {base: SWIPER_NAV_MOBILE.iconW, md: 3},
      },

      sx: {
        '&.swiper-button-prev': {
          left: 0,
        },
        '&.swiper-button-next': {
          right: 0,
        },
        '&.swiper-button-next::after': {
          backgroundImage: SWIPER_INSTOCK_NAV_NEXT,
        },
        '&.swiper-button-prev::after': {
          backgroundImage: SWIPER_INSTOCK_NAV_PREV,
        },
        '&.swiper-button-disabled': {
          cursor: 'default',
          opacity: 0.5,
          pointerEvents: 'initial',
        },
        '&.swiper-button-lock': {
          display: 'none',
        },
      },
    };

    /**
     * Range / blog in-tile image swiper: no arrows on mobile grid; chevrons from md+.
     * `range_tile_carousel_slide` uses `instockTileNavigation` (arrows on mobile, no touch).
     */
    const rangeTileNavigation = {
      ...instockTileNavigation,
      display: {base: 'none', md: 'flex'},
    };

    const instockLikePaginationContainer = {
      inset: 0,
      pointerEvents: 'none',
      position: 'absolute',
      zIndex: 2,
      sx: {
        '& .swiper-button-next, & .swiper-button-prev': {
          pointerEvents: 'auto',
        },
      },
    };

    return {
    /**
     * Styles applied to swiper container
     */
      container: {
        alignItems: instockLikeImageTile ? 'stretch' : 'center',
        display: 'flex',
        flexDirection: instockLikeImageTile ? 'column' : undefined,
        height: instockLikeImageTile ? '100%' : undefined,
        justifyContent: instockLikeImageTile ? 'flex-start' : 'center',
        minHeight: instockLikeImageTile ? 0 : undefined,
        position: instockLikeImageTile ? 'relative' : undefined,
        width: instockLikeImageTile ? '100%' : undefined,
        paddingBottom: options.scrollbar ? 3 : 0,
        userSelect: 'none',

        sx: {
          ...(instockLikeImageTile && {
            '--swiper-theme-color': theme.colors.brandColors.greyscale90,
            '& .swiper-wrapper': {
              alignItems: 'stretch',
              height: '100%',
            },
            '& .swiper-slide': {
              boxSizing: 'border-box',
              height: '100%',
            },
          }),
          ...(isWantItSooner && {
            '& .swiper-wrapper': {
              alignItems: 'flex-start',
            },
            '& .swiper-button-lock': {
              display: 'none',
            },
          }),
          '&:not(.swiper-initialized)': {
          // display: 'none',
          },
        },
      },

      paginationContainer: instockLikeImageTile ? instockLikePaginationContainer : undefined,

      /**
     * Styles applied to individual slides
     */
      slide: instockLikeImageTile ?
        {
          boxSizing: 'border-box',
          display: 'flex',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
        } :
        isWantItSooner ?
          {
            boxSizing: 'border-box',
            flexShrink: 0,
            width: {base: '280px', md: '317px'},
          } :
          {
            // height: 'initial',
          },

      /**
     * @link [Navigation CSS Custom Properties](https://swiperjs.com/swiper-api#navigation-css-custom-properties)
     */
      navigation: instockLikeImageTile ?
        (isInstockTile || isRangeTileCarouselSlide ?
          instockTileNavigation :
          rangeTileNavigation) :
        {
          alignItems: 'center',
          backdropFilter: 'blur(15px)',
          bg: 'brandColors.greyscale20',
          borderRadius: 'full',
          cursor: 'pointer',

          /* Outer list (`want_it_sooner`): nav from md; gallery defaults use lg */
          display: isWantItSooner ? {base: 'none', md: 'flex'} : {base: 'none', lg: 'flex'},
          height: 12.5,
          justifyContent: 'center',
          margin: '0',
          transform: 'translateY(-50%)',
          transition: 'all 0.2s ease',
          width: 12.5,

          _hover: {
            backgroundColor: 'brandColors.greyscale50',
          },

          _after: {
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: SWIPER_NAV_BG.arrow12x14,
            content: '""',
            display: 'block',
            height: '100%',
            width: '100%',
          },

          sx: {
            '&.swiper-button-next::after': {
              backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M3.87485%2012.625L9.23485%207.265C9.26968%207.23022%209.29731%207.18891%209.31617%207.14344C9.33502%207.09797%209.34473%207.04922%209.34473%207C9.34473%206.95078%209.33502%206.90203%209.31617%206.85656C9.29731%206.81109%209.26968%206.76978%209.23485%206.735L3.87485%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
            },

            '&.swiper-button-prev::after': {
              backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M8.12515%2012.625L2.76515%207.265C2.73032%207.23022%202.70269%207.18891%202.68383%207.14344C2.66498%207.09797%202.65527%207.04922%202.65527%207C2.65527%206.95078%202.66498%206.90203%202.68383%206.85656C2.70269%206.81109%202.73032%206.76978%202.76515%206.735L8.12515%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
            },
            '&.swiper-button-disabled': {
              cursor: 'default',
              opacity: 0.5,
              pointerEvents: 'initial',
            },
            '&.swiper-button-lock': {
              display: 'none',
            },
          },
        },

      /**
     * @link [Pagination CSS Custom Properties](https://swiperjs.com/swiper-api#pagination-css-custom-properties)
     */
      pagination: {
        sx: {
          '--swiper-pagination-bullet-horizontal-gap': '0',

          // Bullets Pagination
          '&.swiper-pagination-bullets': {
            backdropFilter: 'blur(15px)',
            backgroundColor: theme.colors.brandColors.greyscale20,
            borderRadius: 12.5,
            bottom: 4,
            display: 'flex',
            gap: 2.5,
            left: '50%',
            paddingX: 2.75,
            paddingY: 2,
            transform: 'translateX(-50%)',
            width: 'auto',
          },

          '.swiper-pagination-bullet': {
            backgroundColor: theme.colors.brandColors.linen03,
            borderRadius: 'full',
            height: 1,
            width: 1,
          },

          '.swiper-pagination-bullet-active': {
            backgroundColor: theme.colors.brandColors.greyscale40,
          },

          // Progressbar Pagination (dark fill = progress, grey track = total)
          ...(isInstockTile || isRangeTile || isRangeTileBlog || isRangeTileCarouselSlide ?
            {
              '&.swiper-pagination-bullets': {
                display: 'none',
              },
              '&.swiper-pagination-progressbar': {
                '--swiper-pagination-progressbar-bg-color': 'transparent',
                '--swiper-theme-color': theme.colors.brandColors.greyscale90,
                'backgroundColor': 'transparent',
                'bottom': 0,
                'height': '2px',
                'left': 0,
                'margin': 0,
                'top': 'auto',
                'transform': 'none',
                'width': '100%',
                'zIndex': 2,
              },
              '.swiper-pagination-progressbar-fill': {
                background: theme.colors.brandColors.greyscale90,
                backgroundColor: theme.colors.brandColors.greyscale90,
              },
            } :
            {
              '&.swiper-pagination-progressbar': {
                backgroundColor: theme.colors.brandColors.disabledBG,
                bottom: {base: 6, lg: 8},
                height: 0.5,
                left: '50%',
                transform: 'translateX(-50%)',
                textAlign: 'left',
                top: 'auto',
                width: 65,
              },

              '.swiper-pagination-progressbar-fill': {
                background: theme.colors.brandColors.greyscale90,
                backgroundColor: theme.colors.brandColors.greyscale90,
              },
            }),
        },
      },

      /**
     * @link [Scrollbar CSS Custom Properties](https://swiperjs.com/swiper-api#scrollbar-css-custom-properties)
     */
      scrollbar: {
        sx: {
          '--swiper-scrollbar-bottom': 0,
        },
      },
    };
  },
  variants: {
    /**
     * Product Tiles outer carousel (CF-049.1 `appearance: Carousel`).
     * Uses the same circular frosted nav button style as `want_it_sooner`.
     * No pagination (mirrors `want_it_sooner`).
     * Nav wrapper uses `display: contents` so it does not collapse to 0×0 (absolutely positioned arrows).
     */
    'product-tiles-carousel': ({theme}) => ({
      paginationContainer: {
        display: 'contents',
      },
      container: {
        overflow: {base: 'hidden', lg: 'unset'},
        userSelect: 'none',
        width: '100%',
        sx: {
          '--swiper-theme-color': theme.colors.brandColors.greyscale90,
          '& .swiper-wrapper': {
            alignItems: 'stretch',
          },
          '& .swiper-slide': {
            boxSizing: 'border-box',
            height: 'auto',
          },
        },
      },
      slide: {
        boxSizing: 'border-box',
        height: 'auto',
        /** Desktop (lg+) CF 4-up (`317px`); mobile: `PRODUCT_TILE_CAROUSEL_MOBILE_SLIDE_WIDTH_PX`. */
        width: {base: `${PRODUCT_TILE_CAROUSEL_MOBILE_SLIDE_WIDTH_PX}px`, lg: '317px'},
      },
      // Circular frosted button — same visual as `want_it_sooner` base nav
      navigation: {
        alignItems: 'center',
        backdropFilter: 'blur(15px)',
        bg: 'brandColors.greyscale20',
        borderRadius: 'full',
        cursor: 'pointer',
        display: {base: 'none', md: 'flex'},
        height: '70px',
        justifyContent: 'center',
        margin: '0',
        pl: '14px',
        pr: '16px',
        pt: '14px',
        pb: '14px',
        transform: 'translateY(-50%)',
        transition: 'all 0.2s ease',
        width: '70px',

        _hover: {
          backgroundColor: 'brandColors.greyscale50',
        },

        _after: {
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: SWIPER_NAV_BG.arrow12x14,
          content: '""',
          display: 'block',
          height: '100%',
          width: '100%',
        },

        sx: {
          '&.swiper-button-next::after': {
            backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M3.87485%2012.625L9.23485%207.265C9.26968%207.23022%209.29731%207.18891%209.31617%207.14344C9.33502%207.09797%209.34473%207.04922%209.34473%207C9.34473%206.95078%209.33502%206.90203%209.31617%206.85656C9.29731%206.81109%209.26968%206.76978%209.23485%206.735L3.87485%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
          '&.swiper-button-prev::after': {
            backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M8.12515%2012.625L2.76515%207.265C2.73032%207.23022%202.70269%207.18891%202.68383%207.14344C2.66498%207.09797%202.65527%207.04922%202.65527%207C2.65527%206.95078%202.66498%206.90203%202.68383%206.85656C2.70269%206.81109%202.73032%206.76978%202.76515%206.735L8.12515%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
          '&.swiper-button-disabled': {
            cursor: 'default',
            opacity: 0.5,
            pointerEvents: 'initial',
          },
          '&.swiper-button-lock': {
            display: 'none',
          },
          '&.swiper-button-prev': {
            left: -8.75,
            right: 'auto',
            top: '40%',
          },
          '&.swiper-button-next': {
            left: 'auto',
            right: -8.75,
            top: '40%',
          },
        },
      },
      pagination: {
        sx: {
          '--swiper-pagination-bullet-horizontal-gap': '0',
          '&.swiper-pagination-bullets': {display: 'none'},
          '&.swiper-pagination-progressbar': {
            '--swiper-pagination-progressbar-bg-color': 'transparent',
            '--swiper-theme-color': theme.colors.brandColors.greyscale90,
            'backgroundColor': 'transparent',
            'bottom': 0,
            'height': '2px',
            'left': 0,
            'margin': 0,
            'top': 'auto',
            'transform': 'none',
            'width': '100%',
            'zIndex': 2,
          },
          '.swiper-pagination-progressbar-fill': {
            background: theme.colors.brandColors.greyscale90,
            backgroundColor: theme.colors.brandColors.greyscale90,
          },
        },
      },
    }),

    /**
     * Product Tiles outer carousel — 3-up desktop (`numberItemsPerView` `'3'` or `'3.2'` via `resolveProductTilesDesktopColumnCount`).
     * Slide width: mobile = `PRODUCT_TILE_CAROUSEL_MOBILE_SLIDE_WIDTH_PX` (shared with 4-up); lg = `424px`.
     * swiper-options sets `slidesPerView: 3` at lg.
     */
    'product-tiles-carousel-3': ({theme}) => ({
      paginationContainer: {
        display: 'contents',
      },
      container: {
        overflow: {base: 'hidden', lg: 'unset'},
        userSelect: 'none',
        width: '100%',
        sx: {
          '--swiper-theme-color': theme.colors.brandColors.greyscale90,
          '& .swiper-wrapper': {
            alignItems: 'stretch',
          },
          '& .swiper-slide': {
            boxSizing: 'border-box',
            height: 'auto',
          },
        },
      },
      slide: {
        boxSizing: 'border-box',
        height: 'auto',
        width: {base: `${PRODUCT_TILE_CAROUSEL_MOBILE_SLIDE_WIDTH_PX}px`, lg: '424px'},
      },
      navigation: {
        alignItems: 'center',
        backdropFilter: 'blur(15px)',
        bg: 'brandColors.greyscale20',
        borderRadius: 'full',
        cursor: 'pointer',
        display: {base: 'none', md: 'flex'},
        height: '70px',
        justifyContent: 'center',
        margin: '0',
        pl: '14px',
        pr: '16px',
        pt: '14px',
        pb: '14px',
        transform: 'translateY(-50%)',
        transition: 'all 0.2s ease',
        width: '70px',

        _hover: {
          backgroundColor: 'brandColors.greyscale50',
        },

        _after: {
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: SWIPER_NAV_BG.arrow12x14,
          content: '""',
          display: 'block',
          height: '100%',
          width: '100%',
        },

        sx: {
          '&.swiper-button-next::after': {
            backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M3.87485%2012.625L9.23485%207.265C9.26968%207.23022%209.29731%207.18891%209.31617%207.14344C9.33502%207.09797%209.34473%207.04922%209.34473%207C9.34473%206.95078%209.33502%206.90203%209.31617%206.85656C9.29731%206.81109%209.26968%206.76978%209.23485%206.735L3.87485%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
          '&.swiper-button-prev::after': {
            backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M8.12515%2012.625L2.76515%207.265C2.73032%207.23022%202.70269%207.18891%202.68383%207.14344C2.66498%207.09797%202.65527%207.04922%202.65527%207C2.65527%206.95078%202.66498%206.90203%202.68383%206.85656C2.70269%206.81109%202.73032%206.76978%202.76515%206.735L8.12515%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
          '&.swiper-button-disabled': {
            cursor: 'default',
            opacity: 0.5,
            pointerEvents: 'initial',
          },
          '&.swiper-button-lock': {
            display: 'none',
          },
          '&.swiper-button-prev': {
            left: -8.75,
            right: 'auto',
            top: '40%',
          },
          '&.swiper-button-next': {
            left: 'auto',
            right: -8.75,
            top: '40%',
          },
        },
      },
      pagination: {
        sx: {
          '--swiper-pagination-bullet-horizontal-gap': '0',
          '&.swiper-pagination-bullets': {display: 'none'},
          '&.swiper-pagination-progressbar': {
            '--swiper-pagination-progressbar-bg-color': 'transparent',
            '--swiper-theme-color': theme.colors.brandColors.greyscale90,
            'backgroundColor': 'transparent',
            'bottom': 0,
            'height': '2px',
            'left': 0,
            'margin': 0,
            'top': 'auto',
            'transform': 'none',
            'width': '100%',
            'zIndex': 2,
          },
          '.swiper-pagination-progressbar-fill': {
            background: theme.colors.brandColors.greyscale90,
            backgroundColor: theme.colors.brandColors.greyscale90,
          },
        },
      },
    }),

    /**
     * @param {Object} args - Component arguments
     * @param {Contentful.MediaCarousel} args.component - MediaCarousel component data
     * @param {boolean} [args.isComplete] - Whether component has all fields
     * @param {boolean} [args.isFetching] - Whether additional data is being fetched
     *
     * @returns {MediaCarouselStyles} - Component styles
     */
    /**
     * CF-010: Media carousel bar – Figma 3132-2273.
     * Desktop: bar gap 18.71px, padding 14px, text 12.472px / letter-spacing 3.742px.
     * Mobile: reduced gap, padding, and typography for compact bar.
     */
    'CF-010': (args) => {
      const paginationFontSize = {base: '2xs', lg: 'xs'};
      const paginationLetterSpacing = {base: '3px', lg: '3.742px'};

      return {
        slide: {
          sx: {
            '& .component--image ': {
              textAlign: 'center',
            },
            '& .swiper-slide .rich-text-content': {
              maxW: '111px',
            },
          },
        },
        paginationContainer: {
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(2px)',
          background: 'rgba(27, 26, 24, 0.4)',
          borderRadius: 1.25,
          bottom: {
            base: 'calc(11px + var(--swiper-cf010-desc-offset, 0px))',
            md: 'calc(32px + var(--swiper-cf010-desc-offset, 0px))',
          },
          display: 'flex',
          flexDirection: 'row',
          borderRadius: '4px',
          gap: {base: 0.5, lg: 4.7},
          paddingX: {base: 3.5, lg: 3.5},
          paddingY: {base: 2.375, lg: 3.5},
          position: 'absolute',
          right: {base: 4, lg: '50%'},
          transform: {base: 'none', lg: 'translateX(50%)'},
          zIndex: {base: 2, lg: 3},
        },

        navigation: {
          alignItems: 'center',
          backdropFilter: 'none',
          backgroundColor: 'transparent',
          display: 'flex',
          height: 2.5,
          justifyContent: 'center',
          minHeight: 2.5,
          minWidth: 2.5,
          position: 'static',
          transform: 'none',
          width: 2.5,
          zIndex: 3,
          _after: {
            content: 'none',
            display: 'none',
          },

          _hover: {
            backgroundColor: 'transparent',
          },

          sx: {
            '&.swiper-button-disabled': {
              cursor: 'default',
              opacity: 0.5,
              pointerEvents: 'initial',
            },
          },
        },

        pagination: {
          alignItems: 'center',
          color: 'white',
          display: 'flex',
          flexBasis: 0,
          fontFamily: 'aktiv-grotesk, sans-serif',
          fontSize: paginationFontSize,
          fontWeight: 500,
          letterSpacing: paginationLetterSpacing,
          lineHeight: 1,
          position: 'static',
          transform: 'none',

          sx: {
            '&.swiper-pagination-fraction': {
              alignItems: 'center',
              display: 'flex',
              fontSize: paginationFontSize,
              fontWeight: 500,
              lineHeight: 1,
            },
            '&.swiper-pagination-fraction .swiper-pagination-current, &.swiper-pagination-fraction .swiper-pagination-total': {
              fontSize: paginationFontSize,
              lineHeight: 1,
            },
            '&.swiper-pagination-fraction *': {
              fontSize: paginationFontSize,
              lineHeight: 1,
            },
          },
        },

        autoplay: {
          bottom: {
            base: 'calc(11px + var(--swiper-cf010-desc-offset, 0px))',
            md: 'calc(37px + var(--swiper-cf010-desc-offset, 0px))',
          },
          left: {base: 4, lg: '37px'},
          position: 'absolute',
          zIndex: 1,
          sx: {
            'padding': {base: 0, md: 0},
            // paddingInlineStart: {base: 0, md: 0},
            '& svg': {
              width: {
                base: SWIPER_CF010_AUTOPLAY_ICON_PX.mobile,
                md: SWIPER_CF010_AUTOPLAY_ICON_PX.desktop,
              },
              height: {
                base: SWIPER_CF010_AUTOPLAY_ICON_PX.mobile,
                md: SWIPER_CF010_AUTOPLAY_ICON_PX.desktop,
              },
            },
          },
        },
      };
    },

    'CF-011': (args) => ({
      root: {
        position: 'relative',
        overflow: 'hidden',
      },
    }),

    'CF-011-Thumbs': (args) => ({
      container: {
        left: {base: 11.25, lg: 20},
        bottom: {base: 12.5, lg: 15.5},
        overflow: {base: 'visible', lg: 'hidden'},
        position: 'absolute',
        width: {base: 'calc(100% - 5.625rem)', lg: 'calc(100% - 10rem)'},
        zIndex: zIndex.zIndices.dropdown,
      },

      slide: {
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderTopColor: 'whiteAlpha.400',
        color: 'whiteAlpha.400',
        cursor: 'pointer',
        fontSize: {base: 'lg', lg: '1.5xl'},
        fontWeight: 500,
        paddingTop: 7,
        minHeight: {base: 17, lg: 21.25},

        sx: {
          '&.swiper-slide-thumb-active': {
            borderTopColor: 'white',
            borderTopWidth: 3,
            color: 'white',
          },

          '.amb-markup': {
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            lineHeight: 'shorter',
          },

          '.amb-markup p': {
            margin: 0,
            color: 'whiteAlpha.400',
            fontFamily: 'heading',
            fontSize: {base: 'lg', lg: '1.5xl'},
            fontWeight: 500,
            lineHeight: 'shorter',
            letterSpacing: {base: 0, lg: '-0.015rem'},
          },
          '.amb-markup p:first-child': {
            fontWeight: 400,
          },

          '&.swiper-slide-thumb-active .amb-markup p': {
            color: 'white',
          },

          '.amb-markup p + .amb-markup p': {
            letterSpacing: {base: 0, lg: '-0.015rem'},
          },

          '&[role="button"]:focus-visible': {
            boxShadow: 'none',
            outline: '2px solid var(--chakra-colors-blue-500)',
            outlineOffset: '2px',
          },

          '&[role="button"]:focus:not(:focus-visible)': {
            outline: 'none',
          },
        },
      },
    }),

    'CF-012': (args) => ({
      /**
       * Row of prev + next: centered; gap 56px mobile / 48px desktop; bottom 40px / 80px (Figma).
       * Icons are custom SVG children — Swiper `::after` hidden in `navigation`.
       */
      paginationContainer: {
        alignItems: 'center',
        bottom: {base: '40px', lg: '80px'},
        display: 'flex',
        flexDirection: 'row',
        gap: {base: '56px', lg: '48px'},
        justifyContent: 'center',
        left: 0,
        pointerEvents: 'none',
        position: 'absolute',
        right: 0,
        zIndex: zIndex.zIndices.dropdown,
        sx: {
          '& .swiper-button-prev, & .swiper-button-next': {
            pointerEvents: 'auto',
          },
        },
      },
      navigation: {
        alignItems: 'center',
        backdropFilter: 'none',
        backgroundColor: 'transparent',
        borderRadius: 0,
        cursor: 'pointer',
        display: 'flex',
        flexShrink: 0,
        height: 'auto',
        justifyContent: 'center',
        margin: 0,
        minH: 0,
        minW: 0,
        padding: 0,
        position: 'static',
        top: 'auto',
        transform: 'none',
        width: 'auto',

        _after: {
          content: 'none',
          display: 'none',
        },

        _hover: {
          backgroundColor: 'transparent',
          opacity: 0.9,
        },

        color: 'white',

        sx: {
          '&.swiper-button-disabled': {
            'color': 'white',
            'cursor': 'default',
            'opacity': 1,
            'pointerEvents': 'none',
            '_hover': {opacity: 1},
            '& svg': {
              color: 'white',
              opacity: 0.5,
              width: '10px',
            },
          },
          '&:not(.swiper-button-disabled) svg': {
            color: 'white',
            opacity: 1,
            width: '10px',
          },
        },
      },
      root: {
        position: 'relative',
      },
      slide: {
        sx: {
          '& .component--video': {
            minHeight: {base: '700px', lg: '870px'},
            height: '100%',
            backgroundColor: 'black',
          },
          // Shop the Look as a main slide: same fill behavior as Image (width + responsive hero img).
          '& .component--shop-the-look': {
            maxWidth: '100%',
            width: '100%',
          },
          '& .component--shop-the-look img': {
            display: 'block',
            height: 'auto',
            maxWidth: '100%',
            width: '100%',
          },
        },
      },
    }),

    /**
     * ACCOUNT-02 — My Account landing suggested-for-you carousel (Figma 5759:50664 / 56823).
     * Mobile 1.3-up · desktop 3-up; slide width follows Swiper (numeric slidesPerView).
     */
    'account-landing-product-tiles': ({theme}) => ({
      paginationContainer: {
        display: 'contents',
      },
      container: {
        overflow: {base: 'hidden', lg: 'unset'},
        userSelect: 'none',
        width: '100%',
        sx: {
          '--swiper-theme-color': theme.colors.brandColors.greyscale90,
          '& .swiper-wrapper': {
            alignItems: 'stretch',
          },
          '& .swiper-slide': {
            boxSizing: 'border-box',
            height: 'auto',
          },
        },
      },
      slide: {
        boxSizing: 'border-box',
        height: 'auto',
        width: '100%',
      },
      navigation: {
        alignItems: 'center',
        backdropFilter: 'blur(15px)',
        bg: 'brandColors.greyscale20',
        borderRadius: 'full',
        cursor: 'pointer',
        display: {base: 'none', lg: 'flex'},
        height: '70px',
        justifyContent: 'center',
        margin: '0',
        pl: '14px',
        pr: '16px',
        pt: '14px',
        pb: '14px',
        transform: 'translateY(-50%)',
        transition: 'all 0.2s ease',
        width: '70px',

        _hover: {
          backgroundColor: 'brandColors.greyscale50',
        },

        _after: {
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: SWIPER_NAV_BG.arrow12x14,
          content: '""',
          display: 'block',
          height: '100%',
          width: '100%',
        },

        sx: {
          '&.swiper-button-next::after': {
            backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M3.87485%2012.625L9.23485%207.265C9.26968%207.23022%209.29731%207.18891%209.31617%207.14344C9.33502%207.09797%209.34473%207.04922%209.34473%207C9.34473%206.95078%209.33502%206.90203%209.31617%206.85656C9.29731%206.81109%209.26968%206.76978%209.23485%206.735L3.87485%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
          '&.swiper-button-prev::after': {
            backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M8.12515%2012.625L2.76515%207.265C2.73032%207.23022%202.70269%207.18891%202.68383%207.14344C2.66498%207.09797%202.65527%207.04922%202.65527%207C2.65527%206.95078%202.66498%206.90203%202.68383%206.85656C2.70269%206.81109%202.73032%206.76978%202.76515%206.735L8.12515%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
          '&.swiper-button-disabled': {
            cursor: 'default',
            opacity: 0.5,
            pointerEvents: 'initial',
          },
          '&.swiper-button-lock': {
            display: 'none',
          },
          '&.swiper-button-prev': {
            left: -8.75,
            right: 'auto',
            top: '36%',
          },
          '&.swiper-button-next': {
            left: 'auto',
            right: -8.75,
            top: '36%',
          },
        },
      },
      pagination: {
        sx: {
          '--swiper-pagination-bullet-horizontal-gap': '0',
          '&.swiper-pagination-bullets': {display: 'none'},
        },
      },
    }),

    /**
     * CF-050 — Blog related articles: white chevrons on semi-transparent black pills over imagery.
     * Disabled arrows keep pointer hit-area and stacking so clicks do not fall through to cards.
     */
    'blog-related-articles': () => ({
      root: {
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
      },
      container: {
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        width: '100%',
      },
      paginationContainer: {
        bottom: 'auto',
        display: 'block',
        left: 0,
        margin: 0,
        paddingX: {base: 0, md: 2},
        pointerEvents: 'none',
        position: 'absolute',
        right: 0,
        top: 0,
        transform: 'none',
        height: 'var(--cf050-cover-h, min(76vw, 360px))',
        width: '100%',
        zIndex: zIndex.zIndices.sticky,
      },
      navigation: {
        alignItems: 'center',
        backdropFilter: 'blur(35px)',
        backgroundColor: 'brandColors.greyscale20',
        borderRadius: 'full',
        cursor: 'pointer',
        display: {base: 'none', lg: 'flex'},
        flexShrink: 0,
        height: 12.5,
        justifyContent: 'center',
        lineHeight: 0,
        margin: 0,
        minH: 0,
        minW: 0,
        padding: 0,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        transition: 'all 0.2s ease',
        width: 12.5,
        zIndex: 1,

        _after: {
          content: 'none',
          display: 'none',
        },

        _hover: {
          backgroundColor: 'brandColors.greyscale50',
        },

        sx: {
          /**
           * Base Swiper theme merges `navigation.sx` with defaults that paint chevrons on
           * `::after` at `width/height: 100%`. Without an explicit override, that pseudo can
           * still win and cover the slide (giant grey arrow). Icons are SVG children (CF-012).
           */
          '&::after, &::before, &.swiper-button-prev::after, &.swiper-button-next::after, &.swiper-button-prev::before, &.swiper-button-next::before': {
            backgroundImage: 'none',
            content: 'none',
            display: 'none',
            fontSize: '0',
            height: '0',
            width: '0',
          },
          '&.swiper-button-prev': {
            left: '1px',
            right: 'auto',
            pointerEvents: 'auto',
          },
          '&.swiper-button-next': {
            left: 'auto',
            right: '1px',
            pointerEvents: 'auto',
          },
          '& > svg': {
            display: 'block',
            flexShrink: 0,
            height: '20px',
            maxHeight: '20px',
            maxWidth: '10px',
            width: '10px',
          },
          '&.swiper-button-lock': {
            display: 'none',
          },
          '&.swiper-button-disabled': {
            display: 'none',
            pointerEvents: 'none',
          },
          '&:not(.swiper-button-disabled) svg': {
            color: 'white',
            height: '20px',
            maxHeight: '20px',
            maxWidth: '10px',
            opacity: 1,
            width: '10px',
          },
        },
      },
      slide: {
        boxSizing: 'border-box',
        height: 'auto',
        width: {base: '276px', lg: 'auto'},
        minHeight: {base: '364px', lg: '530px'},
        paddingBottom: 0,
        sx: {
          '&.swiper-slide': {
            height: 'auto',
            width: {base: '276px', lg: 'auto'},
          },
        },
      },
    }),

    'CF-012-Thumbs': (args) => ({
      container: {
        left: '50%',
        position: 'absolute',
        top: {base: 55, lg: 77.5},
        transform: 'translateX(-50%)',
        width: {base: '100%', lg: 'calc(100% - 21.75rem)'},
        paddingX: {base: 4, lg: 0},
        boxSizing: 'border-box',
        zIndex: zIndex.zIndices.dropdown,
      },

      slide: {
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderTopColor: 'whiteAlpha.400',
        color: 'whiteAlpha.400',
        cursor: 'pointer',
        fontSize: {base: '1xs', lg: 'sm'},
        fontWeight: '400',
        lineHeight: 'shorter',
        paddingTop: 3,
        width: {base: 30, lg: 40},
        minWidth: {base: 30, lg: 40},
        boxSizing: 'border-box',
        sx: {
          '&.swiper-slide': {
            flexShrink: 0,
            opacity: 0.75,
            minHeight: {base: 10, lg: 12},
          },

          '&.swiper-slide-thumb-active': {
            borderTopColor: 'white',
            borderTopWidth: 3,
            color: 'white',
            fontWeight: '700',
            opacity: 1,
          },

          '.amb-markup': {
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5em',
            lineHeight: '20px',
          },

          '.amb-markup p': {
            color: 'white',
            fontFamily: '"Aktiv Grotesk", sans-serif',
            fontSize: {base: '13px', lg: '14px'},
            fontStyle: 'normal',
            fontWeight: 500,
            margin: 0,
          },
          '&.swiper-slide-thumb-active .amb-markup p': {
            fontSize: {base: '13px', lg: '14px'},
            fontWeight: 700,
          },
        },
      },

      navigation: {
        sx: {
          '&.swiper-button-disabled': {
            display: 'none',
          },

          '&.swiper-button-next': {
            right: '0',
          },
          '&.swiper-button-prev': {
            left: '0',
          },
        },
      },
    }),

    'CF-013': (args) => ({
      root: {
        backgroundColor: 'white',
        boxSizing: 'border-box',
        margin: 0,
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        maxWidth: '100vw',
        paddingBottom: {base: '48px', lg: '80px'},
        position: 'relative',
        width: '100vw',
      },
      container: {
        alignItems: 'stretch',
        boxSizing: 'border-box',
        justifyContent: 'flex-start',
        marginX: 0,
        maxWidth: '100%',
        paddingX: 0,
        width: '100%',
      },
      slide: {
        boxSizing: 'border-box',
        height: 'auto',
        maxWidth: '100%',
        width: '100%',
        sx: {
          '&.swiper-slide': {
            width: '100%',
          },
          '& .component--carousel-item': {
            maxWidth: '100%',
            width: '100%',
          },
          '& .component--shop-the-look': {
            maxWidth: '100%',
            width: '100%',
          },
          '& .component--image, & .component--image img, & picture img, & img': {
            display: 'block',
            height: 'auto',
            maxWidth: '100%',
            width: '100%',
          },
        },
      },
    }),

    'CF-013-Thumbs': (args) => ({
      container: {
        marginTop: {base: '16px', lg: 'unset'},
        bottom: {base: 0, lg: 0},
        left: '50%',
        position: {'base': 'relative', 'lg': 'absolute'},
        transform: 'translateX(-50%)',
        width: '100%',
        height: {base: 10.75, lg: 15.5},
      },
      slide: {
        cursor: 'pointer',
        height: {base: 10.75, lg: 15.5},
        opacity: 0.5,
        width: {base: 12.5, lg: 15},
        sx: {
          '& .component--image, & .component--image picture, & .component--image img, & picture img, & img': {
            display: 'block',
            height: '100%',
            width: '100%',
          },
          '& .component--image img, & picture img, & img': {
            objectFit: 'contain',
            objectPosition: 'top center',
          },

          /**
           * Media Carousel: Thumbs module sets `swiper-slide-thumb-active`.
           * King Difference (curtain main, no linked main Swiper): Swiper sets `swiper-slide-active` on `slideTo`.
           */
          '&.swiper-slide-active, &.swiper-slide-thumb-active': {
            opacity: 1,
          },

          '&[role="button"]:focus-visible': {
            boxShadow: 'none',
            outline: '2px solid var(--chakra-colors-blue-500)',
            outlineOffset: '2px',
          },

          '&[role="button"]:focus:not(:focus-visible)': {
            outline: 'none',
          },
        },
      },
    }),

    'CF-016': (args) => ({
      root: {
        overflow: 'hidden',
        width: '100%',
      },
      container: {
        width: '100%',
        paddingLeft: {base: 16, lg: 20},
        paddingBottom: {base: 15, lg: 30},

        sx: {
          '& .swiper-wrapper': {
            alignItems: 'flex-start',
          },
        },
      },
      slide: {
        flexShrink: 0,
        boxSizing: 'border-box',
        width: 'auto',
        maxWidth: 'none',
        minHeight: 'unset',
        overflow: 'visible',
        minHeight: {base: '240px', lg: '360px'},
        overflow: 'hidden',
        sx: {
          '&.swiper-slide:has(.component--video)': {
            maxWidth: 'clamp(240px, 65vw, 640px)',
            width: '100%',
            height: '100%',
          },
          '& .component--video': {
            backgroundColor: 'black',
            height: '100%',
            maxH: {base: '240px', lg: '360px'},
          },
          '& .component--video > *': {
            top: '50%',
            transform: 'translateY(-50%)',
          },
          '& .component--image': {
            width: 'auto',
            maxWidth: '100%',
            height: 'auto',
          },
          '& .component--image img': {
            width: 'auto',
            maxWidth: 'clamp(240px, 65vw, 727px)',
            height: {base: '240px', lg: '360px'},
            objectFit: 'cover',
            display: 'block',
          },
          '&:has(.component--shop-the-look)': {
            minHeight: {base: '231px', lg: '495px'},
          },
          '& .component--shop-the-look img': {
            maxWidth: 'clamp(240px, 65vw, 727px)',
            height: {base: '231px', lg: '495px'},
          },
        },
      },
    }),

    'CF-022': (args) => ({
      root: {
        bg: 'brandColors.primaryBG',
        overflow: 'hidden',
        py: {base: 10, md: 16},
        width: '100%',
      },
      paginationContainer: {
        display: 'contents',
      },
      container: {
        marginX: 'auto',
        maxWidth: 'container.xxl',
        overflow: 'visible',
        paddingBottom: {base: 2, md: 4},
        paddingLeft: {base: 5, md: 20},
        paddingRight: {base: 5, md: 20},
        position: 'relative',
      },
      slide: {
        boxSizing: 'border-box',
        flexShrink: 0,
        height: 'auto',
        paddingBottom: 0,
        maxWidth: {base: '217px', md: '280px'},
        sx: {
          '& .component--carousel-item': {
            height: '100%',
          },
          // CF-022 Image slides: 3:4 card; caption uses default image.js (below image).
          // Homepage overlay caption → home-page-global.js (body.page--home-page).
          '& .component--image .chakra-image, & .component--image img': {
            aspectRatio: '3/4',
            display: 'block',
            height: 'auto',
            objectFit: 'cover',
            width: '100%',
          },
        },
      },
      navigation: {
        alignItems: 'center',
        backdropFilter: 'blur(15px)',
        bg: 'brandColors.greyscale20',
        borderRadius: 'full',
        cursor: 'pointer',
        display: {base: 'none', md: 'flex'},
        height: '70px',
        justifyContent: 'center',
        margin: 0,
        pb: '14px',
        pl: '14px',
        position: 'absolute',
        pr: '16px',
        pt: '14px',
        top: '50%',
        transform: 'translateY(-50%)',
        transition: 'all 0.2s ease',
        width: '70px',
        zIndex: 2,

        _hover: {
          backgroundColor: 'brandColors.greyscale50',
        },

        _after: {
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '12px 12px',
          content: '""',
          display: 'block',
          height: '12px',
          width: '12px',
        },

        sx: {
          '&.swiper-button-next::after': {
            backgroundImage:
              'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M3.87485%2012.625L9.23485%207.265C9.26968%207.23022%209.29731%207.18891%209.31617%207.14344C9.33502%207.09797%209.34473%207.04922%209.34473%207C9.34473%206.95078%209.33502%206.90203%209.31617%206.85656C9.29731%206.81109%209.26968%206.76978%209.23485%206.735L3.87485%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
          '&.swiper-button-prev::after': {
            backgroundImage:
              'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M8.12515%2012.625L2.76515%207.265C2.73032%207.23022%202.70269%207.18891%202.68383%207.14344C2.66498%207.09797%202.65527%207.04922%202.65527%207C2.65527%206.95078%202.66498%206.90203%202.68383%206.85656C2.70269%206.81109%202.73032%206.76978%202.76515%206.735L8.12515%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
          '&.swiper-button-disabled': {
            cursor: 'default',
            opacity: 0.5,
            pointerEvents: 'none',
            _hover: {
              backgroundColor: 'brandColors.greyscale20',
            },
          },
          '&.swiper-button-lock': {
            display: 'none',
          },
          '&.swiper-button-prev': {
            left: '-38px',
            right: 'auto',
          },
          '&.swiper-button-next': {
            left: 'auto',
            right: '-38px',
          },
        },
      },
    }),

    /**
     * CF-057 — Collections-style rail (same cards as CF-022) with bottom-right circular scroll buttons.
     * Nav row sits below the slides (flex order), not overlaid on the sides.
     */
    'CF-057': () => ({
      root: {
        bg: 'brandColors.primaryBG',
        overflow: 'hidden',
        py: {base: 10, md: 16},
        width: '100%',
      },
      container: {
        display: 'flex',
        flexDirection: 'column',
        marginX: 'auto',
        maxWidth: 'container.xxl',
        overflow: 'visible',
        paddingLeft: {base: 5, md: 20},
        paddingRight: {base: 5, md: 20},
        position: 'relative',
        width: '100%',
        sx: {
          '& > .swiper-wrapper': {
            order: 1,
          },
        },
      },
      slide: {
        boxSizing: 'border-box',
        flexShrink: 0,
        height: 'auto',
        paddingBottom: 0,
        maxWidth: {base: '217px', md: '280px'},
        sx: {
          '& .component--carousel-item': {
            height: '100%',
          },
          '& .component--image .chakra-image, & .component--image img': {
            aspectRatio: '3/4',
            display: 'block',
            height: 'auto',
            objectFit: 'cover',
            width: '100%',
          },
        },
      },
      paginationContainer: {
        alignItems: 'center',
        display: {base: 'none', md: 'flex'},
        flexDirection: 'row',
        gap: 3,
        justifyContent: 'flex-end',
        marginTop: {md: 8},
        order: 2,
        position: 'static',
        width: '100%',
        zIndex: 2,
      },
      navigation: {
        alignItems: 'center',
        bg: 'brandColors.greyscale20',
        borderRadius: 'full',
        cursor: 'pointer',
        display: 'flex',
        flexShrink: 0,
        height: 12,
        justifyContent: 'center',
        margin: 0,
        padding: 0,
        position: 'static',
        top: 'auto',
        transform: 'none',
        transition: 'background-color 0.2s ease',
        width: 12,
        zIndex: 2,

        _hover: {
          backgroundColor: 'brandColors.greyscale30',
        },

        _after: {
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '12px 14px',
          content: '""',
          display: 'block',
          height: '14px',
          width: '12px',
        },

        sx: {
          '&.swiper-button-next::after': {
            backgroundImage:
              'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M3.87485%2012.625L9.23485%207.265C9.26968%207.23022%209.29731%207.18891%209.31617%207.14344C9.33502%207.09797%209.34473%207.04922%209.34473%207C9.34473%206.95078%209.33502%206.90203%209.31617%206.85656C9.29731%206.81109%209.26968%206.76978%209.23485%206.735L3.87485%201.375%22%20stroke%3D%22%231B1A18%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
          '&.swiper-button-prev::after': {
            backgroundImage:
              'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M8.12515%2012.625L2.76515%207.265C2.73032%207.23022%202.70269%207.18891%202.68383%207.14344C2.66498%207.09797%202.65527%207.04922%202.65527%207C2.65527%206.95078%202.66498%206.90203%202.68383%206.85656C2.70269%206.81109%202.73032%206.76978%202.76515%206.735L8.12515%201.375%22%20stroke%3D%22%231B1A18%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
          '&.swiper-button-disabled': {
            cursor: 'default',
            opacity: 0.4,
            pointerEvents: 'none',
            _hover: {
              backgroundColor: 'brandColors.greyscale20',
            },
          },
          '&.swiper-button-lock': {
            display: 'none',
          },
          '&.swiper-button-prev, &.swiper-button-next': {
            left: 'auto',
            right: 'auto',
          },
        },
      },
    }),

    /**
     * CF-046 Media Carousel: block layout (not flex-centered .swiper); full-width max container;
     * mobile gutter token 5 (20px); desktop nav at viewport inset via calc; arrows lg+ only.
     *
     * Image sizing (Contentful):
     * - Slide / image track height is fixed (210 / 500) so the carousel never grows vertically.
     * - Default slide width is Design 280 / 647 when CMS width is unset.
     * - Author `Image.width` sets `--kl-image-display-width` on that image only (see image.jsx);
     *   height stays capped via maxHeight so mixed widths do not change carousel height.
     */
    'CF-046': (args) => ({
      root: {
        overflowX: 'hidden',
        overflowY: 'hidden',
        width: '100%',
      },
      container: {
        display: 'block',
        marginX: 'auto',
        maxWidth: 'container.xxl',
        overflow: 'visible',
        paddingLeft: {base: 5, lg: 20},
        paddingRight: {base: 5, lg: 20},
        position: 'relative',
        width: '100%',
      },
      slide: {
        boxSizing: 'border-box',
        flexShrink: 0,
        width: 'auto',
        height: {base: '210px', md: '500px'},
        maxHeight: {base: '210px', md: '500px'},
        overflow: 'hidden',
        sx: {
          '& .component--image': {
            // Defaults; overridden per-image when Image sets --kl-image-display-width inline.
            '--kl-image-display-width': {base: '100%', md: '647px'},
            display: 'block',
            height: '100%',
            maxHeight: '100%',
            width: 'var(--kl-image-display-width)',
          },
          '& .component--image img': {
            display: 'block',
            width: '100%',
            height: '100%',
            maxHeight: {base: '210px', md: '500px'},
            objectFit: 'cover',
          },
        },
      },
      paginationContainer: {
        display: 'contents',
        position: 'static',
      },
      navigation: {
        display: {base: 'none', lg: 'flex'},
        height: 17.5,
        width: 17.5,
        position: 'absolute',
        top: '50%',
        zIndex: 2,
        sx: {
          '&.swiper-button-prev': {
            left: '44px',
            right: 'auto',
          },
          '&.swiper-button-next': {
            right: '44px',
            left: 'auto',
          },
          // watchOverflow → isLocked → lockClass (CMS loop: false, slides fit viewport)
          '&.swiper-button-lock': {
            display: 'none',
          },
        },
      },
    }),

    'cover-swiper': () => ({
      container: {
        overflow: 'unset',
        position: 'relative',
        width: '100%',
      },
      slide: {
        boxSizing: 'border-box',
        flexShrink: 0,
        maxWidth: '120px',
        width: 'auto !important',
      },
      navigation: {
        backdropFilter: 'blur(15px)',
        bg: 'rgba(27, 26, 24, 0.2)',
        display: {base: 'none', lg: 'flex'},
        sx: {
          '&.swiper-button-lock, &.swiper-button-disabled': {
            display: 'none',
          },
          '&.swiper-button-prev': {
            left: '-16px',
            right: 'auto',
          },
          '&.swiper-button-next': {
            left: 'auto',
            right: '-14px',
          },
        },
      },
    }),

    /**
     * Configuration thumbnail swiper (View details - configuration-gallery-block)
     * Design: img 230px, arrows 30px stick out on both sides.
     */
    'configuration-thumbnail': ({theme}) => ({
      container: {
        position: 'relative',
        width: {base: '100%'},
        overflow: 'hidden',
        paddingBottom: {base: 0, lg: 0},
        marginBottom: {base: 8, lg: 0},
        paddingX: 3,
        boxSizing: 'border-box',
      },
      paginationContainer: {
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        left: 0,
        right: 0,
        zIndex: 2,
      },
      slide: {
        width: {base: '100%', lg: 57.5},
        flexShrink: 0,
        boxSizing: 'border-box',
      },
      navigation: {
        display: 'flex',
        height: 7.5,
        width: 7.5,
        _after: {
          backgroundSize: SWIPER_NAV_BG.thumb14,
        },
        sx: {
          '&.swiper-button-prev': {
            left: {base: '5px', lg: 0},
            right: 'auto',
          },
          '&.swiper-button-next': {
            right: {base: '5px', lg: 0},
            left: 'auto',
          },
          '&.swiper-button-lock': {
            display: 'none',
          },
        },
      },
      pagination: {
        justifyContent: 'center',
        sx: {
          '.swiper-pagination-bullet': {
            backgroundColor: theme.colors.brandColors.linen03,
          },
          '.swiper-pagination-bullet-active': {
            backgroundColor: theme.colors.brandColors.greyscale40,
          },
        },
      },
    }),

    /**
     * Showroom details — horizontal anchor link strip (Contentful related pages).
     * Mobile + desktop: nav visible; lg default Swiper hides arrows on small screens.
     */
    'anchor-links': () => ({
      paginationContainer: {
        display: 'contents',
      },
      container: {
        minH: '30px',
        overflow: 'hidden',
        position: 'relative',
      },
      slide: {
        flexShrink: 0,
        width: 'auto',
      },
      navigation: {
        alignItems: 'center',
        bg: 'brandColors.greyscale20',
        borderRadius: 'full',
        boxShadow: 'none',
        color: 'white',
        cursor: 'pointer',
        display: 'flex',
        flexShrink: 0,
        height: '30px',
        justifyContent: 'center',
        margin: 0,
        minH: '30px',
        minW: '30px',
        p: 0,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        transition: 'background-color 0.2s ease',
        width: '30px',
        zIndex: 11,
        border: 'none',

        _hover: {
          backgroundColor: 'brandColors.greyscale30',
        },

        _after: {
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '12px 14px',
          content: '""',
          display: 'block',
          flexShrink: 0,
          height: '14px',
          margin: '0 auto',
          width: '12px',
        },

        sx: {
          '&.swiper-button-prev': {
            left: '4px',
            right: 'auto',
          },
          '&.swiper-button-next': {
            left: 'auto',
            right: '4px',
          },
          '&.swiper-button-next::after': {
            backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M3.87485%2012.625L9.23485%207.265C9.26968%207.23022%209.29731%207.18891%209.31617%207.14344C9.33502%207.09797%209.34473%207.04922%209.34473%207C9.34473%206.95078%209.33502%206.90203%209.31617%206.85656C9.29731%206.81109%209.26968%206.76978%209.23485%206.735L3.87485%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
          '&.swiper-button-prev::after': {
            backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M8.12515%2012.625L2.76515%207.265C2.73032%207.23022%202.70269%207.18891%202.68383%207.14344C2.66498%207.09797%202.65527%207.04922%202.65527%207C2.65527%206.95078%202.66498%206.90203%202.68383%206.85656C2.70269%206.81109%202.73032%206.76978%202.76515%206.735L8.12515%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
          '&.swiper-button-disabled': {
            display: 'none',
          },
          '&.swiper-button-lock': {
            display: 'none',
          },
        },
      },
    }),

    /**
     * Account Navigation - Mobile horizontal swiper
     * Non-continuous carousel with circular navigation buttons
     */
    'account-nav': () => ({
      container: {
        overflow: 'visible',
        position: 'relative',
        width: '100%',
      },
      slide: {
        flexShrink: 0,
        width: 'auto',
      },
      paginationContainer: {
        bottom: 'auto',
        display: 'contents',
        position: 'static',
      },
      navigation: {
        alignItems: 'center',
        backgroundColor: 'brandColors.greyscale10',
        borderRadius: '50%',
        display: 'flex',
        height: 8,
        justifyContent: 'center',
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        width: 8,
        zIndex: 10,

        _hover: {
          backgroundColor: 'brandColors.greyscale20',
        },

        _after: {
          color: 'brandColors.greyscale90',
          fontSize: 'xs',
        },

        sx: {
          // Position prev button on left
          '&.swiper-button-prev': {
            left: -2.5,
            marginTop: -2,
          },
          // Position next button on right
          '&.swiper-button-next': {
            right: -2.5,
            marginTop: -2,
          },
          // Hide disabled navigation arrows
          '&.swiper-button-disabled': {
            opacity: 0,
            pointerEvents: 'none',
            visibility: 'hidden',
          },
        },
      },
    }),
  },
});
